import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import UserService from '../services/UserService';
import './DealDetailPage.css';
import './EditSubDealPage.css';

// Admin page: edit or delete the sub-deals (products) of one deal.
// Replaces the unfinished EditSubDeal.js (kept in the project, not used anymore).

const emptyForm = {
  title: '', link: '', price: '', regularprice: '', discription: '', promocode: '', active: '', imagefile: null
};

function EditSubDealPage() {
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState(false);
  const [editingId, setEditingId] = useState(null);   // which sub-deal is open for editing
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null);       // preview of a newly chosen photo
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null); // sub-deal waiting for "Yes, delete"
  const [message, setMessage] = useState(null);       // { type: 'ok' | 'error', text }
  // new: "Add sub-deal" form
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [addPreview, setAddPreview] = useState(null);
  const [addSaving, setAddSaving] = useState(false);

  const loadDeal = () => {
    UserService.getDealDetail(id)
      .then((res) => setDeal(res.data))
      .catch((err) => { console.error('Failed to load deal', err); setError(true); });
  };

  useEffect(loadDeal, [id]);

  const startEdit = (sub) => {
    setEditingId(sub.subdeal_id);
    setDeletingId(null);
    setMessage(null);
    setPreview(null);
    setForm({
      title: sub.title || '',
      link: sub.link || '',
      price: sub.price || '',
      regularprice: sub.regularprice || '',
      discription: sub.discription || '',
      promocode: sub.promocode || '',
      active: sub.active || '',
      imagefile: null
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setPreview(null);
  };

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const changeImage = (e) => {
    const file = e.target.files[0] || null;
    setForm({ ...form, imagefile: file });
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const save = (e) => {
    e.preventDefault();
    if (form.title.trim() === '' || form.link.trim() === '') {
      setMessage({ type: 'error', text: 'Title and link are required.' });
      return;
    }
    const data = new FormData();
    ['title', 'link', 'price', 'regularprice', 'discription', 'promocode', 'active'].forEach((key) => data.append(key, form[key]));
    if (form.imagefile) data.append('imagefile', form.imagefile);

    setSaving(true);
    UserService.updateSubDeal(editingId, data)
      .then(() => {
        setMessage({ type: 'ok', text: 'Sub-deal saved.' });
        setEditingId(null);
        setPreview(null);
        loadDeal();
      })
      .catch((err) => {
        console.error('Sub-deal update failed', err);
        const status = err.response && err.response.status;
        setMessage({
          type: 'error',
          text: status === 401 || status === 403
            ? 'Not saved: you are logged out. Please log in again.'
            : 'Not saved. The server may be waking up, please wait a minute and try again.'
        });
      })
      .finally(() => setSaving(false));
  };

  // new: add a sub-deal to this deal
  const openAdd = () => {
    setAdding(true);
    setEditingId(null);
    setDeletingId(null);
    setMessage(null);
    setAddForm(emptyForm);
    setAddPreview(null);
  };

  const changeAdd = (e) => setAddForm({ ...addForm, [e.target.name]: e.target.value });

  const changeAddImage = (e) => {
    const file = e.target.files[0] || null;
    setAddForm({ ...addForm, imagefile: file });
    setAddPreview(file ? URL.createObjectURL(file) : null);
  };

  const saveNew = (e) => {
    e.preventDefault();
    if (!addForm.imagefile) {
      setMessage({ type: 'error', text: 'Please choose a photo for the new sub-deal.' });
      return;
    }
    if (addForm.title.trim() === '' || addForm.link.trim() === '') {
      setMessage({ type: 'error', text: 'Title and link are required.' });
      return;
    }
    // The backend needs ALL of these fields (it rejects the sub-deal if one is missing)
    const data = new FormData();
    data.append('link', addForm.link);
    data.append('imagefile', addForm.imagefile);
    data.append('title', addForm.title);
    data.append('price', addForm.price);
    data.append('regularprice', addForm.regularprice);
    data.append('subdealdiscription', addForm.discription);
    data.append('promocode', addForm.promocode);
    data.append('active', addForm.active);
    data.append('endingdate', '');

    setAddSaving(true);
    UserService.addSubDeal(id, data)
      .then(() => {
        setMessage({ type: 'ok', text: 'Sub-deal added.' });
        setAdding(false);
        setAddPreview(null);
        loadDeal();
      })
      .catch((err) => {
        console.error('Add sub-deal failed', err);
        const status = err.response && err.response.status;
        setMessage({
          type: 'error',
          text: status === 401 || status === 403
            ? 'Not added: you are logged out. Please log in again.'
            : 'Not added (error ' + (status || 'no answer') + '). Please try again.'
        });
      })
      .finally(() => setAddSaving(false));
  };

  const confirmDelete = (subId) => {
    UserService.deleteSubDeal(subId)
      .then(() => {
        setMessage({ type: 'ok', text: 'Sub-deal deleted.' });
        setDeletingId(null);
        loadDeal();
      })
      .catch((err) => {
        console.error('Sub-deal delete failed', err);
        setMessage({ type: 'error', text: 'Not deleted. Please try again (are you still logged in?).' });
      });
  };

  if (error) {
    return <div className="detail-page"><div className="detail-message"><p>Could not load this deal.</p><Link to="/user/editdeals">← Back to Edit Deals</Link></div></div>;
  }
  if (!deal) {
    return <div className="detail-page"><div className="detail-message"><Spinner animation="border" /><p>Loading deal...</p></div></div>;
  }

  const subdeals = deal.subdeal || [];

  return (
    <div className="detail-page">
      <div className="detail-layout page-narrow">
        <main>
          <nav className="detail-breadcrumb" aria-label="Breadcrumb">
            <Link to="/user/dashboard">Dashboard</Link>
            <span>›</span>
            <Link to="/user/editdeals">Edit Deals</Link>
            <span>›</span>
            <span>Sub-deals</span>
          </nav>

          <section className="page-card esd-deal">
            <img src={deal.imageurl} alt="" />
            <div>
              <h1>{(deal.dealcommondiscription || '').trim()}</h1>
              <p className="page-intro">{deal.store} · {deal.category} · {subdeals.length} sub-deal{subdeals.length === 1 ? '' : 's'}</p>
            </div>
          </section>

          {message && <p className={message.type === 'ok' ? 'esd-msg ok' : 'esd-msg error'}>{message.text}</p>}

          {subdeals.length === 0 && <p className="page-card">This deal has no sub-deals yet.</p>}

          {subdeals.map((sub) => (
            <section key={sub.subdeal_id} className="page-card esd-sub">
              {editingId === sub.subdeal_id ? (
                <form className="page-form" onSubmit={save}>
                  <div className="esd-photo-edit">
                    <img src={preview || sub.imageurl} alt="" />
                    <label>
                      New photo (optional — leave empty to keep this one)
                      <input type="file" accept="image/*" onChange={changeImage} />
                    </label>
                  </div>
                  <label>Title<input name="title" value={form.title} onChange={change} required /></label>
                  <label>Link<input name="link" value={form.link} onChange={change} required /></label>
                  <div className="esd-row">
                    <label>Price<input name="price" value={form.price} onChange={change} placeholder="16.49" /></label>
                    <label>Regular price<input name="regularprice" value={form.regularprice} onChange={change} placeholder="34.99" /></label>
                  </div>
                  <label>Description<input name="discription" value={form.discription} onChange={change} /></label>
                  <div className="esd-row">
                    <label>Promo code<input name="promocode" value={form.promocode} onChange={change} /></label>
                    <label>
                      Status
                      <select name="active" value={form.active} onChange={change}>
                        <option value="">(not set)</option>
                        <option value="active">active</option>
                        <option value="inactive">inactive</option>
                      </select>
                    </label>
                  </div>
                  <div className="esd-buttons">
                    <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    <button type="button" className="esd-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
                  </div>
                </form>
              ) : (
                <div className="esd-view">
                  <img src={sub.imageurl} alt="" />
                  <div className="esd-info">
                    <h2>{sub.title}</h2>
                    <p>
                      <strong>${sub.price}</strong>
                      {sub.regularprice && <span className="esd-reg">Reg. ${sub.regularprice}</span>}
                      {sub.active && <span className="esd-status">{sub.active}</span>}
                    </p>
                    {sub.discription && <p>{sub.discription}</p>}
                    {sub.promocode && <p>Code: <code>{sub.promocode}</code></p>}
                    <p className="esd-link"><a href={sub.link} target="_blank" rel="noreferrer">{sub.link}</a></p>

                    {deletingId === sub.subdeal_id ? (
                      <div className="esd-confirm">
                        <span>Delete this sub-deal?</span>
                        <button type="button" className="esd-danger" onClick={() => confirmDelete(sub.subdeal_id)}>Yes, delete</button>
                        <button type="button" className="esd-secondary" onClick={() => setDeletingId(null)}>No</button>
                      </div>
                    ) : (
                      <div className="esd-buttons">
                        <button type="button" onClick={() => startEdit(sub)}>Edit</button>
                        <button type="button" className="esd-danger" onClick={() => { setDeletingId(sub.subdeal_id); setEditingId(null); }}>Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          ))}

          {/* new: add another sub-deal to this deal */}
          {adding ? (
            <section className="page-card esd-sub esd-add">
              <h2 className="esd-add-title">Add a sub-deal</h2>
              <form className="page-form" onSubmit={saveNew}>
                <div className="esd-photo-edit">
                  {addPreview && <img src={addPreview} alt="" />}
                  <label>
                    Photo (required)
                    <input type="file" accept="image/*" onChange={changeAddImage} />
                  </label>
                </div>
                <label>Title<input name="title" value={addForm.title} onChange={changeAdd} required /></label>
                <label>Link<input name="link" value={addForm.link} onChange={changeAdd} required placeholder="https://amzn.to/..." /></label>
                <div className="esd-row">
                  <label>Price<input name="price" value={addForm.price} onChange={changeAdd} placeholder="16.49" /></label>
                  <label>Regular price<input name="regularprice" value={addForm.regularprice} onChange={changeAdd} placeholder="34.99" /></label>
                </div>
                <label>Description<input name="discription" value={addForm.discription} onChange={changeAdd} /></label>
                <div className="esd-row">
                  <label>Promo code<input name="promocode" value={addForm.promocode} onChange={changeAdd} /></label>
                  <label>
                    Status
                    <select name="active" value={addForm.active} onChange={changeAdd}>
                      <option value="">(not set)</option>
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                    </select>
                  </label>
                </div>
                <div className="esd-buttons">
                  <button type="submit" disabled={addSaving}>{addSaving ? 'Adding...' : 'Add Sub-deal'}</button>
                  <button type="button" className="esd-secondary" onClick={() => setAdding(false)} disabled={addSaving}>Cancel</button>
                </div>
              </form>
            </section>
          ) : (
            <div className="esd-buttons esd-add-open">
              <button type="button" onClick={openAdd}>+ Add sub-deal</button>
            </div>
          )}

          <p><Link to="/user/editdeals">← Back to Edit Deals</Link></p>
        </main>
      </div>
    </div>
  );
}

export default EditSubDealPage;
